// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Whether SN labels may be printed here (component design §12): in production only.
 *
 * An SN generated on staging or a laptop comes from that environment's own counters, so it
 * can repeat one production has already issued. Printed and stuck on a machine, it would
 * put a second device under a real SN. Until the server has said where this is, nothing
 * prints, and nothing is marked as a test either — the answer is not known yet.
 *
 * A local copy does not print either, but its codes carry no test mark: a laptop's screens
 * are what screenshots for guides and tutorials are taken from, and a stamp across every
 * QR code there would end up in all of them. Staging, which people open to try things out,
 * keeps the mark.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/endpoints";

export interface LabelPrinting {
  printable: boolean;
  /** This is known to be a test environment other than a local copy: show the codes as
   *  test codes. */
  isTest: boolean;
}

export function useLabelPrinting(): LabelPrinting {
  const policy = useQuery({
    queryKey: ["client-policy"],
    queryFn: api.clientPolicy,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return {
    printable: policy.data?.labels_printable === true,
    isTest:
      policy.data !== undefined &&
      !policy.data.labels_printable &&
      policy.data.environment !== "local",
  };
}
